import { useTranslation } from 'react-i18next';
import { useMemo, useState, type FC } from 'react';
import ChevronBottom from 'components/atoms/icons/chevron-bottom';
import classNames from 'classnames';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import useUser from 'utils/useUser';
import BaseDropdownButton from 'components/atoms/baseButton/dropdown';
import { useCountdownTimer } from 'infrastructure/hooks/use-count-down-timer';
import CountdownTimer from 'components/atoms/countdown-timer';
import { Flex, Tooltip, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { getAssignToolQueryKey } from 'infrastructure/hooks/rpm/assign-tool/use-get-assign-tool';

import s from './styles.module.scss';
import { getAssignMenuItems } from './utils';

import type { TDurationInMinutes } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import type { IAssignee } from 'infrastructure/interfaces';

const { Text } = Typography;

interface AssignCellProps {
  patientGuid: string;
  assignee?: IAssignee;
  isInsideAssignedDrawer?: boolean;
}
const AssignCell: FC<AssignCellProps> = ({
  patientGuid,
  assignee,
  isInsideAssignedDrawer = false,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { loginGuid } = useUser();
  const { assignPatient, isLoading } = useAssignPatient();
  const queryClient = useQueryClient();

  const isAssignedToOther = assignee && assignee.loginGuid !== loginGuid;
  const isAssignedToMe = assignee && assignee.loginGuid === loginGuid;
  const endTime = assignee?.expireAtTimestamp
    ? new Date(assignee.expireAtTimestamp)
    : undefined;

  useCountdownTimer({
    endTime,
    onFinish: () => {
      if (isInsideAssignedDrawer) {
        const queryKey = getAssignToolQueryKey();
        queryClient.invalidateQueries(queryKey);
      } else {
        assignPatient({ patientGuid, action: 'unassign', silentUpdate: true });
      }
    },
  });

  const label =
    assignee && isAssignedToMe ? t('labels.unassign') : t('labels.assign');

  const onItemClick = async (durationInMinutes: TDurationInMinutes) => {
    assignPatient({ patientGuid, durationInMinutes, action: 'assign' });
  };

  const handleUnassign = async () => {
    if (!assignee || isAssignedToOther) return;

    assignPatient({ patientGuid, action: 'unassign' });
  };

  const items = useMemo(() => getAssignMenuItems(t, onItemClick), [t]);

  return (
    <Flex
      vertical
      justify="center"
      align="center"
      className={assignee && s.wrapper}
      onClick={(e) => e.stopPropagation()}
    >
      <BaseDropdownButton
        items={[]}
        dropdownProps={{
          menu: { items },
          open,
          onOpenChange: setOpen,
          placement: 'bottomRight',
          disabled: !!assignee,
          rootClassName: s.menu,
          trigger: ['click'],
        }}
        label={label}
        type="secondary"
        disabled={isLoading || !!isAssignedToOther}
        rightIcon={isAssignedToMe || isLoading ? undefined : <ChevronBottom />}
        loading={isLoading}
        onClick={handleUnassign}
        className={classNames(s['assign-button'], {
          [s.open]: open,
          [s['without-icon']]: !(!isAssignedToMe && !isLoading),
          [s['assigned-to-me']]: isAssignedToMe,
          [s.loading]: isLoading,
        })}
      />
      {assignee && isAssignedToMe && endTime && (
        <CountdownTimer endTime={endTime} isNegative className={s.countdown} />
      )}
      {assignee && isAssignedToOther && (
        <Tooltip placement="bottom" title={assignee?.fullName ?? ''}>
          <Text className={s.assigned}>{assignee?.fullName ?? ''}</Text>
        </Tooltip>
      )}
    </Flex>
  );
};
export default AssignCell;
